import { useForm } from "react-hook-form";
import ControlledInput from "Components/Input";
import { useTranslation } from "react-i18next";
import Button from "Components/Button";
import Space from "Components/Space";
import FlexRow from "Components/FlexRow";
import React, { useEffect, useState } from "react";
import { useMutateProject } from "Apis/ProjectApi";
import {IMutateProjectDto, IProjectDto} from "Apis/Models/IProjectDto";
import FileUpload from "../../../Components/FileUpload/FileUpload";
import styles from './ProjectForm.module.scss';
import { IFileInformationDto } from "Apis/Models/IFileDto";
import Loader from "../../../Assets/SVG/Loader";
import Select from "../../../Components/Select";
import ControlledSelect from "../../../Components/Select/ControlledSelect";
import {useCustomers} from "../../../Apis/CustomerApi";
import useFilteredList from "../../../Hooks/UseFilteredList";
import {usePartners} from "../../../Apis/PartnerApi";
import Label from "../../../Components/Label";
import ControlledTextArea from "../../../Components/TextArea";
import {useUsers} from "../../../Apis/UsersApi";
import {useTags} from "../../../Apis/TagApi";

interface IProps {
    project?: IProjectDto;
    onSubmit?: (data: IMutateProjectDto) => void;
}
const ProjectForm = (props: IProps) => {

    const { t } = useTranslation();

    const { data: customers } = useCustomers({includeAll: true});
    const { data: partners } = usePartners({includeAll: true});
    const { data: users } = useUsers({includeAll: true});
    const { data: tags } = useTags({includeAll: true});

    const { control, handleSubmit, reset } = useForm<IMutateProjectDto>();
    const [file, setFile] = useState<IFileInformationDto>();
    const { mutateAsync, isLoading } = useMutateProject();

    const [ filteredCustomers, setCustomerFilter ] = useFilteredList(customers?.items ?? [], x => [x.name, x.vatNumber]);
    const [ filteredReferrals, setReferralFilter ] = useFilteredList(partners?.items ?? [], x => [x.name, x.vatNumber]);
    const [ filteredTags, setTagFilter ] = useFilteredList(tags?.items ?? [], x => [x.name]);

    const [ filteredResponsible, setResponsibleFilter ] = useFilteredList(users?.items ?? [], x => [x.name]);

    useEffect(() => {
        if (props.project) {
            reset({ ...props.project, tags : props.project.tags.map(x => x.id) ?? [] });
            if (props.project.image) {
                setFile(props.project.image);
            }
        }
    }, [reset, props.project])

    const onSubmit = async(data: IMutateProjectDto) => {
        await mutateAsync({ ...data, image: file });

        if (props.onSubmit) {
            props.onSubmit(data);
        }

        reset();
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Space direction={"vertical"} size={"large"}>

                <div className={styles.imageRow}>
                    <FileUpload callback={setFile} accept={['.jpeg', '.jpg', '.png']} render={loading => (
                        <div className={styles.fileUpload}>
                            {loading ? (<Loader color={"red"} height={50} width={50} />) : (<>
                                {!!file ? (
                                    <img height={150} width={150} src={file.url} alt={file.fileName} />
                                ) : (
                                    <>
                                        {t('upload')}
                                    </>
                                )}
                            </>)}
                        </div>
                    )} />
                    <ControlledInput rules={{required: true}} name={"name"} control={control} placeholder={t('name')} />
                </div>

                <ControlledInput rules={{required: true}} name={"initials"} control={control} placeholder={t('initials')} />

                <ControlledSelect rules={{required: true}} control={control} placeholder={t('customer')} name={"customerId"} onFilter={setCustomerFilter}>
                    {filteredCustomers?.map(x => <Select.Option key={x.id} label={x.name} value={x.id} />)}
                </ControlledSelect>

                <ControlledInput name={"att"} control={control} placeholder={t('att')} />
                <ControlledInput name={"address"} control={control} placeholder={t('address')} />
                <ControlledInput name={"zip"} control={control} placeholder={t('zip')} />
                <ControlledInput name={"city"} control={control} placeholder={t('city')} />
                <ControlledInput name={"email"} control={control} placeholder={t('email')} />
                <ControlledInput name={"phone"} control={control} placeholder={t('phone')} />
                <div>
                    <Label placeholder={t('projectNote')} />
                    <ControlledTextArea rows={4} control={control} name={"note"} defaultValue={props.project?.note} />
                </div>

                <ControlledInput name={"paymentInformation.accountRegistration"} type={"number"} control={control} placeholder={t('accountRegistrationNumber')} />
                <ControlledInput name={"paymentInformation.accountNumber"} type={"number"} control={control} placeholder={t('accountNumber')} />

                <ControlledSelect control={control} multiple={true} placeholder={t('projectTags')} name={"tags"} onFilter={setTagFilter}>
                    {filteredTags?.map(x => <Select.Option key={x.id} label={x.name} value={x.id} />)}
                </ControlledSelect>

                <ControlledSelect control={control} placeholder={t('referral')} name={"referralId"} onFilter={setReferralFilter}>
                    {filteredReferrals?.map(x => <Select.Option key={x.id} label={x.name} value={x.id} />)}
                </ControlledSelect>
                <ControlledSelect control={control} placeholder={t('responsible')} name={"responsibleUserId"} onFilter={setResponsibleFilter}>
                    {filteredResponsible?.map(x => <Select.Option key={x.id} label={x.name} value={x.id} />)}
                </ControlledSelect>

                <FlexRow justify={"end"}>
                    <Button buttonType={"submit"} type={"primary"} text={t('save')} loading={isLoading} />
                </FlexRow>
            </Space>
        </form>
    )
}

export default ProjectForm
